/**
 * Agent calibration — the reliability table (migration 829; PRD-value-
 * suggestion-agent.md §6.1).
 *
 * "Confidence is a claim about how often the agent is right when it feels
 * this sure. Uncalibrated, it is decoration." Once a bucket holds enough
 * reviewed proposals, the confidence an agent shows is the bucket's accepted
 * rate, not the model's self-report. The table is written only by
 * `rebuild_agent_calibration()`; this module reads it and does the arithmetic.
 */

import { getSupabaseClient } from './supabaseClient';

import type { ConfidenceBucket } from '../../supabase/functions/_shared/estimateValueCore.ts';
export { confidenceBucket, calibrateConfidence, MIN_REVIEWS_FOR_CALIBRATION, type ConfidenceBucket } from '../../supabase/functions/_shared/estimateValueCore.ts';

export interface CalibrationRow {
  agentType: string;
  field: string;
  bucket: ConfidenceBucket;
  reviewedCount: number;
  acceptedUnchangedCount: number;
  acceptedWithChangeCount: number;
  rejectedCount: number;
  reversedCount: number;
  expiredCount: number;
  acceptedRate: number | null;
  realizedCount: number;
  realizedMeanError: number | null;
  computedAt: string;
}

function rowToCalibration(row: Record<string, unknown>): CalibrationRow {
  return {
    agentType: row.agent_type as string,
    field: row.field as string,
    bucket: row.bucket as ConfidenceBucket,
    reviewedCount: Number(row.reviewed_count ?? 0),
    acceptedUnchangedCount: Number(row.accepted_unchanged_count ?? 0),
    acceptedWithChangeCount: Number(row.accepted_with_change_count ?? 0),
    rejectedCount: Number(row.rejected_count ?? 0),
    reversedCount: Number(row.reversed_count ?? 0),
    expiredCount: Number(row.expired_count ?? 0),
    acceptedRate: row.accepted_rate == null ? null : Number(row.accepted_rate),
    realizedCount: Number(row.realized_count ?? 0),
    realizedMeanError: row.realized_mean_error == null ? null : Number(row.realized_mean_error),
    computedAt: row.computed_at as string,
  };
}

export async function loadAgentCalibration(workspaceId: string, agentType: string): Promise<CalibrationRow[]> {
  const { data, error } = await getSupabaseClient()
    .from('agent_calibration')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('agent_type', agentType);
  if (error) {
    console.warn('[agentCalibration] load failed:', error.message);
    return [];
  }
  return ((data ?? []) as Record<string, unknown>[]).map(rowToCalibration);
}

/** Recompute a workspace's rows now (the nightly job does this too). */
export async function rebuildAgentCalibration(workspaceId: string, agentType: string): Promise<{ rows: number; error: string | null }> {
  const { data, error } = await getSupabaseClient().rpc('rebuild_agent_calibration', { p_workspace_id: workspaceId, p_agent_type: agentType });
  if (error) return { rows: 0, error: error.message };
  return { rows: Number(data ?? 0), error: null };
}
